import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Toolbox, Calculator, Download, CheckCircle, WarningCircle } from "@phosphor-icons/react";

/* ============================================================
   BUS BAR SIZING CARD · v2.82
   ------------------------------------------------------------
   NEC (CDA #22) or IEC standard palette · copper or aluminum ·
   ambient / enclosure / emissivity / temp-rise deratings · 1–4
   parallel bars · short-circuit thermal withstand (IEC 60865
   K factors). Recommended bar + full candidate table + PE
   stampable PDF.
   ============================================================ */
const STANDARDS = [
  { value: "NEC", label: "NEC / CDA #22 (US, imperial)" },
  { value: "IEC", label: "IEC 60439 / 61439 (metric)" },
];
const MATERIALS = [
  { value: "copper", label: "Copper (Cu)" },
  { value: "aluminum", label: "Aluminum (Al)" },
];
const ENCLOSURES = [
  { value: "open_air", label: "Open air (× 1.00)" },
  { value: "ventilated_switchgear", label: "Ventilated switchgear (× 0.85)" },
  { value: "enclosed_switchgear", label: "Enclosed indoor switchgear (× 0.75)" },
  { value: "ip54_enclosure", label: "IP54 enclosure (× 0.65)" },
  { value: "ip65_enclosure", label: "IP65 enclosure (× 0.55)" },
];
const EMISSIVITIES = [
  { value: "painted_black", label: "Painted (× 1.00)" },
  { value: "bright_unpainted", label: "Bright / unpainted (× 0.75)" },
];
const RISES = [
  { value: 30, label: "30 °C (NEMA switchgear)" },
  { value: 45, label: "45 °C" },
  { value: 65, label: "65 °C (IEC 61439 typical)" },
];

export default function BusBarSizingCard() {
  const [form, setForm] = useState({
    standard: "NEC",
    material: "copper",
    continuous_current_amps: 2000,
    ambient_c: 40,
    temp_rise_c: 65,
    enclosure: "enclosed_switchgear",
    emissivity: "painted_black",
    parallel_bars: 1,
    isc_ka: 65,
    sc_duration_s: 1.0,
    // v2.86 · Load-driven sizing (optional). When loads_enabled, the
    // backend derives continuous_current_amps from the loads[] list.
    loads_enabled: false,
    bus_voltage: 480,
    bus_phases: 3,
    harmonics_factor: 1.0,
    loads: [
      { tag: "M-1", load_kw: 50, quantity: 1, load_type: "motor", continuous: true, demand_factor: 1.0, diversity_factor: 1.0, power_factor: 0.9 },
      { tag: "VFD-1", load_kw: 75, quantity: 1, load_type: "vfd_motor", continuous: true, demand_factor: 1.0, diversity_factor: 1.0, power_factor: 0.9 },
      { tag: "LIGHTS", load_amps: 40, quantity: 1, load_type: "lighting", continuous: true, demand_factor: 1.0, diversity_factor: 1.0 },
    ],
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfMeta, setPdfMeta] = useState({
    project_name: "", engineer_name: "", engineer_pe_license: "",
  });
  // Result becomes stale whenever inputs change — mirrors the
  // ConductorSizingCard "recompute before print" contract.
  const [stale, setStale] = useState(false);

  const setField = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setStale(true);
  };

  const buildPayload = () => {
    // v2.86 · Only send `loads` when the user has explicitly opted in
    // — otherwise fall back to the manual `continuous_current_amps`.
    const p = { ...form };
    if (!form.loads_enabled) delete p.loads;
    delete p.loads_enabled;
    return p;
  };

  const compute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/calculators/bus-bar-sizing", buildPayload());
      setResult(data);
      setStale(false);
      // v2.84 · Publish the last computed bus bar geometry so the
      // arc-flash tab can offer a one-click import (gap-mm / electrode
      // config / bolted-fault kA / clearing time all derived from the
      // switchgear tier this bus lives in).
      if (data.recommended) {
        try {
          window.sessionStorage.setItem("eengdes:last_bus_bar_calc", JSON.stringify({
            standard: data.input.standard,
            material: data.input.material,
            enclosure: data.input.enclosure,
            isc_ka: data.input.isc_ka,
            sc_duration_s: data.input.sc_duration_s,
            recommended: {
              label: data.recommended.label,
              area_mm2: data.recommended.area_mm2,
              derated_ampacity_A: data.recommended.derated_ampacity_A,
            },
            ts: Date.now(),
          }));
        } catch { /* sessionStorage may be blocked — non-fatal */ }
        toast.success(`Recommended: ${data.recommended.label} · ${data.recommended.derated_ampacity_A} A`);
      } else {
        toast.warning("No standard bar meets the spec — increase parallel bars or step up material.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Compute failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.post(
        "/calculators/bus-bar-sizing/pe-sheet",
        { ...buildPayload(), ...pdfMeta },
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (pdfMeta.project_name || "busbar").replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 40) || "busbar";
      a.download = `bus_bar_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Bus bar PE calc sheet downloaded");
    } catch {
      toast.error("Failed to render PE sheet");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="border border-white/10 bg-[#12151C] p-6" data-testid="busbar-card">
      <div className="flex items-center gap-2 mb-1">
        <Toolbox weight="fill" size={18} className="text-[#00E5FF]" />
        <div className="label-tag">// Bus bar sizing</div>
      </div>
      <h3 className="font-display text-2xl font-bold">Bus bar sizing · CDA #22 / IEC 61439</h3>
      <p className="text-sm text-gray-400 mt-1">
        Continuous ampacity + short-circuit thermal withstand for standard flat bars. Empirical CDA #22 fit (±5 %) with IEC 60865 K-factor SC check. Screening tool — final design must match the switchgear manufacturer&apos;s type-test rating.
      </p>

      {/* v2.86 · Load builder — optional; when enabled overrides the
          Continuous current (A) input by summing tabulated loads through
          the continuous / demand / diversity / VFD-uplift chain plus a
          global harmonics factor. */}
      <div className="mt-5 border border-[#FFCC00]/25 bg-[#FFCC00]/[0.03] p-3" data-testid="busbar-load-panel">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.loads_enabled}
            onChange={(e) => setField({ loads_enabled: e.target.checked })}
            className="accent-[#FFCC00]"
            data-testid="busbar-loads-enable" />
          <span className="label-tag text-[#FFCC00]">// Compute from loads (VFD-aware · demand · diversity · harmonics)</span>
        </label>
        {form.loads_enabled && (
          <>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Bus voltage (V)">
                <NumInput value={form.bus_voltage} onChange={(v) => setField({ bus_voltage: Number(v) })} testid="busbar-bus-v" />
              </Field>
              <Field label="Phases">
                <Select value={form.bus_phases} onChange={(v) => setField({ bus_phases: Number(v) })}
                  options={[{ value: 1, label: "1φ" }, { value: 3, label: "3φ" }]}
                  testid="busbar-bus-phases" />
              </Field>
              <Field label="Harmonics factor (THDF)" hint="Global uplift: 1.00 (linear) → 1.35 (VFD-dominated). 1.10 is a common LV screwdriver value.">
                <NumInput value={form.harmonics_factor} step="0.05"
                  onChange={(v) => setField({ harmonics_factor: Number(v) })}
                  testid="busbar-harmonics" />
              </Field>
            </div>
            <div className="mt-3 overflow-x-auto border border-white/10" data-testid="busbar-load-table">
              <table className="w-full text-xs font-mono">
                <thead className="bg-[#0A0C10] text-gray-400">
                  <tr className="border-b border-white/10">
                    <BTh>Tag</BTh>
                    <BTh>Type</BTh>
                    <BTh className="w-24">kW</BTh>
                    <BTh className="w-24">or A</BTh>
                    <BTh className="w-16">PF</BTh>
                    <BTh className="w-14">Qty</BTh>
                    <BTh className="w-14">Cont</BTh>
                    <BTh className="w-16">Demand</BTh>
                    <BTh className="w-16">Diversity</BTh>
                    <BTh className="w-14"></BTh>
                  </tr>
                </thead>
                <tbody>
                  {(form.loads || []).map((L, i) => (
                    <tr key={i} className="border-b border-white/5" data-testid={`busbar-load-${i}`}>
                      <BTd><LoadCell value={L.tag} onChange={(v) => updateLoad(setField, form.loads, i, { tag: v })} testid={`busbar-load-${i}-tag`} /></BTd>
                      <BTd>
                        <LoadSelect value={L.load_type}
                          onChange={(v) => updateLoad(setField, form.loads, i, { load_type: v })}
                          options={[
                            { value: "motor",       label: "Motor" },
                            { value: "vfd_motor",   label: "VFD Motor" },
                            { value: "lighting",    label: "Lighting" },
                            { value: "receptacle",  label: "Receptacle" },
                            { value: "hvac",        label: "HVAC" },
                            { value: "other",       label: "Other" },
                          ]}
                          testid={`busbar-load-${i}-type`} />
                      </BTd>
                      <BTd><LoadCell type="number" value={L.load_kw ?? ""} onChange={(v) => updateLoad(setField, form.loads, i, { load_kw: v === "" ? null : Number(v), load_amps: null })} testid={`busbar-load-${i}-kw`} /></BTd>
                      <BTd><LoadCell type="number" value={L.load_amps ?? ""} onChange={(v) => updateLoad(setField, form.loads, i, { load_amps: v === "" ? null : Number(v), load_kw: null })} testid={`busbar-load-${i}-amps`} /></BTd>
                      <BTd><LoadCell type="number" step="0.01" value={L.power_factor ?? 0.9} onChange={(v) => updateLoad(setField, form.loads, i, { power_factor: Number(v) })} testid={`busbar-load-${i}-pf`} /></BTd>
                      <BTd><LoadCell type="number" value={L.quantity ?? 1} onChange={(v) => updateLoad(setField, form.loads, i, { quantity: Math.max(1, Number(v)) })} testid={`busbar-load-${i}-qty`} /></BTd>
                      <BTd className="text-center"><input type="checkbox" checked={L.continuous} onChange={(e) => updateLoad(setField, form.loads, i, { continuous: e.target.checked })} data-testid={`busbar-load-${i}-cont`} /></BTd>
                      <BTd><LoadCell type="number" step="0.05" value={L.demand_factor ?? 1.0} onChange={(v) => updateLoad(setField, form.loads, i, { demand_factor: Number(v) })} testid={`busbar-load-${i}-demand`} /></BTd>
                      <BTd><LoadCell type="number" step="0.05" value={L.diversity_factor ?? 1.0} onChange={(v) => updateLoad(setField, form.loads, i, { diversity_factor: Number(v) })} testid={`busbar-load-${i}-diversity`} /></BTd>
                      <BTd>
                        <button onClick={() => setField({ loads: form.loads.filter((_, j) => j !== i) })}
                          disabled={form.loads.length <= 1}
                          className="text-gray-400 hover:text-[#FF3B30] disabled:opacity-30"
                          data-testid={`busbar-load-${i}-remove`}>×</button>
                      </BTd>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-2 flex items-center gap-3">
              <button onClick={() => setField({ loads: [...form.loads, { tag: `L-${form.loads.length + 1}`, load_kw: 10, quantity: 1, load_type: "other", continuous: true, demand_factor: 1.0, diversity_factor: 1.0, power_factor: 0.9 }] })}
                className="border border-[#FFCC00]/40 text-[#FFCC00] px-3 py-1 text-xs font-mono hover:bg-[#FFCC00]/10"
                data-testid="busbar-load-add">+ Add load</button>
              <div className="ml-auto text-xs font-mono text-gray-500">
                Values below (Continuous current) are ignored while loads are enabled.
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Inputs ── */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Standard">
          <Select value={form.standard}
            onChange={(v) => setField({ standard: v })}
            options={STANDARDS} testid="busbar-standard" />
        </Field>
        <Field label="Material">
          <Select value={form.material}
            onChange={(v) => setField({ material: v })}
            options={MATERIALS} testid="busbar-material" />
        </Field>
        <Field label="Continuous current (A)"
          hint={form.loads_enabled ? "Ignored — derived from loads table above." : undefined}>
          <NumInput value={form.continuous_current_amps}
            onChange={(v) => setField({ continuous_current_amps: Number(v) })}
            testid="busbar-current" />
        </Field>
        <Field label="Ambient temperature (°C)">
          <NumInput value={form.ambient_c}
            onChange={(v) => setField({ ambient_c: Number(v) })}
            testid="busbar-ambient" />
        </Field>
        <Field label="Max temperature rise (°C)">
          <Select value={form.temp_rise_c}
            onChange={(v) => setField({ temp_rise_c: Number(v) })}
            options={RISES} testid="busbar-rise" />
        </Field>
        <Field label="Enclosure type">
          <Select value={form.enclosure}
            onChange={(v) => setField({ enclosure: v })}
            options={ENCLOSURES} testid="busbar-enclosure" />
        </Field>
        <Field label="Surface finish">
          <Select value={form.emissivity}
            onChange={(v) => setField({ emissivity: v })}
            options={EMISSIVITIES} testid="busbar-emissivity" />
        </Field>
        <Field label="Bars per phase (parallel)">
          <Select value={form.parallel_bars}
            onChange={(v) => setField({ parallel_bars: Number(v) })}
            options={[
              { value: 1, label: "1 (× 1.00)" },
              { value: 2, label: "2 (× 1.60)" },
              { value: 3, label: "3 (× 2.10)" },
              { value: 4, label: "4 (× 2.50)" },
            ]} testid="busbar-parallel" />
        </Field>
        <div />
        <Field label="Fault current Isc (kA)"
          hint="Optional — set to 0 to skip SC withstand check.">
          <NumInput value={form.isc_ka}
            onChange={(v) => setField({ isc_ka: Number(v) })}
            testid="busbar-isc" />
        </Field>
        <Field label="Clearing time (s)"
          hint="Typically 1 s for LV, 0.25–0.5 s for MV.">
          <NumInput value={form.sc_duration_s} step="0.05"
            onChange={(v) => setField({ sc_duration_s: Number(v) })}
            testid="busbar-tclear" />
        </Field>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button onClick={compute} disabled={busy}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="busbar-compute">
          <Calculator size={16} /> {busy ? "Computing…" : "Compute"}
        </button>
      </div>

      {/* ── Result ── */}
      {result && (
        <div className="mt-6 space-y-4" data-testid="busbar-result">
          {/* v2.86 · Derived current banner (when load-driven) */}
          {result.load_breakdown && (
            <div className="border border-[#FFCC00]/40 bg-[#FFCC00]/[0.05] p-3" data-testid="busbar-derived-current">
              <div className="label-tag text-[#FFCC00] mb-1">// derived from loads</div>
              <div className="grid grid-cols-3 gap-3 text-xs font-mono">
                <div>
                  <div className="text-[9px] uppercase text-gray-500">Connected</div>
                  <div className="text-lg text-white">{result.load_breakdown.total_connected_amps} A</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase text-gray-500">After cont/demand/div/uplift</div>
                  <div className="text-lg text-white">{result.load_breakdown.total_effective_amps_pre_harmonics} A</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase text-gray-500">Bus current (× harmonics {result.load_breakdown.harmonics_factor})</div>
                  <div className="text-xl font-bold text-[#FFCC00]" data-testid="busbar-derived-bus">{result.load_breakdown.bus_current_amps} A</div>
                </div>
              </div>
            </div>
          )}
          {result.recommended ? (
            <RecommendedCard rec={result.recommended} input={result.input} sc={result.short_circuit} />
          ) : (
            <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/[0.05] p-4 text-sm font-mono text-[#FF8A85]"
              data-testid="busbar-no-pass">
              <WarningCircle size={18} className="inline mr-2" />
              No standard bar in the {result.input.standard} palette meets the spec. Try increasing parallel bars, switching from aluminum to copper, or reducing enclosure derating.
            </div>
          )}
          <CandidatesTable candidates={result.candidates} sc={result.short_circuit} standard={result.input.standard} />

          {/* PE PDF export */}
          <div className="border border-[#00E5FF]/20 bg-[#00E5FF]/[0.03] p-4">
            <div className="label-tag mb-2">// PE calc sheet (stampable PDF)</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <TextInput label="Project name" value={pdfMeta.project_name}
                onChange={(v) => setPdfMeta((m) => ({ ...m, project_name: v }))}
                placeholder="MCC-01 main horizontal bus" testid="busbar-pdf-project" />
              <TextInput label="Reviewing engineer" value={pdfMeta.engineer_name}
                onChange={(v) => setPdfMeta((m) => ({ ...m, engineer_name: v }))}
                placeholder="J. Smith, PE" testid="busbar-pdf-engineer" />
              <TextInput label="PE license #" value={pdfMeta.engineer_pe_license}
                onChange={(v) => setPdfMeta((m) => ({ ...m, engineer_pe_license: v }))}
                placeholder="TX 123456" testid="busbar-pdf-license" />
            </div>
            <div className="mt-3 flex gap-3">
              <button onClick={downloadPdf}
                disabled={pdfBusy || stale}
                className="border border-[#00E5FF]/40 text-[#00E5FF] px-4 py-2 text-sm font-mono hover:bg-[#00E5FF]/10 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                data-testid="busbar-pdf-download"
                title={stale ? "Recompute before printing — inputs changed since last compute." : "Download the stampable PE calc sheet."}>
                <Download size={14} /> {pdfBusy ? "Rendering…" : "Print PE calc sheet"}
              </button>
              {stale && (
                <span className="text-xs font-mono text-[#FFCC00]" data-testid="busbar-stale">
                  ⚠ inputs changed — recompute before print
                </span>
              )}
            </div>
          </div>

          <div className="text-[10px] text-gray-500 font-mono space-y-0.5">
            {result.notes.map((n, i) => <div key={i}>• {n}</div>)}
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────── sub-components ──────────────── */
function RecommendedCard({ rec, input, sc }) {
  return (
    <div className="border border-[#00E5FF]/40 bg-[#00E5FF]/[0.05] p-4" data-testid="busbar-recommended">
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle weight="fill" size={16} className="text-[#00E5FF]" />
        <div className="label-tag text-[#00E5FF]">// recommended bar</div>
      </div>
      <div className="font-display text-3xl font-bold text-white" data-testid="busbar-rec-label">
        {rec.label} {input.material === "copper" ? "Cu" : "Al"}
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
        <Stat label="Cross-section (per bar)" value={`${rec.area_mm2} mm²`} testid="busbar-rec-area" />
        <Stat label="Derated ampacity" value={`${rec.derated_ampacity_A} A`}
          hint={`${rec.ampacity_headroom_pct >= 0 ? "+" : ""}${rec.ampacity_headroom_pct}% headroom`}
          accent={rec.ampacity_headroom_pct >= 10 ? "cyan" : rec.ampacity_headroom_pct >= 0 ? "yellow" : "red"}
          testid="busbar-rec-ampacity" />
        <Stat label="Current density" value={`${rec.current_density_A_per_mm2} A/mm²`} testid="busbar-rec-density" />
        <Stat label="Weight" value={`${rec.weight_kg_per_m} kg/m`} testid="busbar-rec-weight" />
      </div>
      <div className="mt-3 text-[10px] font-mono text-gray-400">
        Derating breakdown: ambient × {rec.factors.ambient} · rise × {rec.factors.temp_rise} · enclosure × {rec.factors.enclosure} · finish × {rec.factors.emissivity} · parallel × {rec.factors.parallel_bars} = <span className="text-white">× {rec.factors.combined}</span>
      </div>
      {input.include_sc && (
        <div className="mt-2 text-[11px] font-mono text-gray-300" data-testid="busbar-rec-sc">
          SC thermal: <span className="text-[#00E5FF]">{sc.required_min_area_mm2} mm²</span> required · <span className="text-white">{rec.area_mm2} mm²</span> provided (K = {sc.K_factor}, Isc {sc.isc_ka} kA / {sc.duration_s} s)
        </div>
      )}
    </div>
  );
}

function CandidatesTable({ candidates, sc, standard }) {
  return (
    <div className="border border-white/10 overflow-x-auto" data-testid="busbar-candidates">
      <div className="bg-[#0A0C10] px-3 py-2 flex items-center gap-2 border-b border-white/10">
        <div className="label-tag">// candidate palette · {standard}</div>
        <div className="ml-auto text-[10px] font-mono text-gray-500">
          {candidates.filter((c) => c.overall_ok).length}/{candidates.length} pass
        </div>
      </div>
      <table className="w-full text-xs font-mono">
        <thead className="bg-[#0A0C10] text-gray-400">
          <tr className="border-b border-white/10">
            <Th>Bar</Th>
            <Th className="text-right">Area mm²</Th>
            <Th className="text-right">Ampacity A</Th>
            <Th className="text-right">Headroom %</Th>
            {sc.required_min_area_mm2 != null && <Th className="text-right">SC min mm²</Th>}
            <Th className="text-center w-14">Status</Th>
          </tr>
        </thead>
        <tbody>
          {candidates.map((c) => (
            <tr key={c.label}
              className={`border-b border-white/5 ${c.overall_ok ? "bg-[#00E5FF]/[0.03]" : "opacity-70"}`}
              data-testid={`busbar-cand-${c.label.replace(/[^a-zA-Z0-9]/g, "_")}`}>
              <Td>{c.label}</Td>
              <Td className="text-right">{c.area_mm2}</Td>
              <Td className="text-right">{c.derated_ampacity_A}</Td>
              <Td className={`text-right ${c.ampacity_headroom_pct < 0 ? "text-[#FF8A85]" : c.ampacity_headroom_pct < 10 ? "text-[#FFCC00]" : "text-[#00E5FF]"}`}>
                {c.ampacity_headroom_pct >= 0 ? "+" : ""}{c.ampacity_headroom_pct}%
              </Td>
              {sc.required_min_area_mm2 != null && (
                <Td className={`text-right ${c.sc_ok ? "" : "text-[#FF8A85]"}`}>
                  {sc.required_min_area_mm2}
                </Td>
              )}
              <Td className="text-center">
                {c.overall_ok
                  ? <CheckCircle size={14} weight="fill" className="inline text-[#00E5FF]" />
                  : !c.ampacity_ok
                    ? <span className="text-[#FF8A85] text-[10px]" title="Ampacity fail">A✗</span>
                    : <span className="text-[#FFCC00] text-[10px]" title="Short-circuit fail">S✗</span>}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="bg-[#0A0C10] border-t border-white/10 px-3 py-2 text-[10px] font-mono text-gray-500">
        Green ✓ = passes ampacity <span className="mx-1">·</span> A✗ = ampacity fail <span className="mx-1">·</span> S✗ = SC withstand fail
      </div>
    </div>
  );
}

/* ──────────────── primitives ──────────────── */
const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="label-tag block mb-1">{label}</span>
    {children}
    {hint && <span className="text-[10px] text-gray-500 mt-1 block">{hint}</span>}
  </label>
);
const NumInput = ({ value, onChange, step = "any", testid }) => (
  <input type="number" step={step} value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
    data-testid={testid} />
);
const TextInput = ({ label, value, onChange, placeholder, testid }) => (
  <label className="block">
    <span className="label-tag block mb-1">{label}</span>
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
      data-testid={testid} />
  </label>
);
const Select = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)}
    className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
    data-testid={testid}>
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);
const Th = ({ children, className = "" }) => (
  <th className={`text-left font-normal px-3 py-2 text-[10px] uppercase tracking-wider ${className}`}>
    {children}
  </th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-3 py-1.5 align-middle ${className}`}>{children}</td>
);
function Stat({ label, value, hint, accent, testid }) {
  const color = accent === "cyan" ? "text-[#00E5FF]" :
                accent === "yellow" ? "text-[#FFCC00]" :
                accent === "red" ? "text-[#FF8A85]" : "text-white";
  return (
    <div className="border border-white/10 bg-black/40 p-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`text-base font-bold ${color}`}>{value}</div>
      {hint && <div className="text-[9px] text-gray-500">{hint}</div>}
    </div>
  );
}

/* v2.86 · Load-builder table primitives (kept private — see the
   `Compute from loads` panel above). */
const BTh = ({ children, className = "" }) => (
  <th className={`text-left font-normal px-2 py-1.5 text-[10px] uppercase tracking-wider ${className}`}>{children}</th>
);
const BTd = ({ children, className = "" }) => (
  <td className={`px-1.5 py-1 align-middle ${className}`}>{children}</td>
);
const LoadCell = ({ value, onChange, type = "text", step, testid }) => (
  <input type={type} step={step} value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    className="w-full bg-black/40 border border-white/10 px-1 py-1 text-xs font-mono focus:border-[#FFCC00] outline-none"
    data-testid={testid} />
);
const LoadSelect = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)}
    className="w-full bg-black/40 border border-white/10 px-1 py-1 text-xs font-mono focus:border-[#FFCC00] outline-none"
    data-testid={testid}>
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);
function updateLoad(setField, loads, idx, patch) {
  setField({ loads: loads.map((L, j) => (j === idx ? { ...L, ...patch } : L)) });
}
